import { useMemo } from "react";
import { cn } from "@repo/utils";
import { useMultipleWorkflowFields } from "../api/use-workflow-fields";
import type { Field, Item, Task, FieldData } from "../types";
import { ItemMetaTable } from "./item-meta-table";
import type { UseFieldMutations, UseTasksInfo } from "./types";

type Props = {
  items: Item[];
  tasks?: Task[];
  isLoading?: boolean;
  isDark?: boolean;
  apiBaseUrl: string;
  windowId?: string;
  useFieldMutations: UseFieldMutations;
  useTasksInfo: UseTasksInfo;
};

export const ItemMetaView = ({
  items,
  tasks,
  isLoading = false,
  isDark = false,
  apiBaseUrl,
  windowId,
  useFieldMutations,
  useTasksInfo,
}: Props) => {
  const workflowIdKey = useMemo(
    () =>
      Array.from(new Set(items.map((i) => i.workflowId).filter(Boolean)))
        .sort((a, b) => a - b)
        .join(","),
    [items]
  );

  const uniqueWorkflowIds = useMemo(
    () => (workflowIdKey ? workflowIdKey.split(",").map(Number) : []),
    [workflowIdKey]
  );

  const {
    workflowFieldsMap,
    isLoading: isLoadingFields,
    isReady: areFieldsReady,
  } = useMultipleWorkflowFields(uniqueWorkflowIds);

  const displayFields = useMemo<FieldData[]>(() => {
    const fieldMap = new Map<number, { values: Set<string>; itemIds: Set<number>; field: Field }>();

    items.forEach((item) => {
      const workflowFields = workflowFieldsMap.get(item.workflowId);
      if (!workflowFields?.length) return;

      workflowFields.forEach((workflowField) => {
        if (!fieldMap.has(workflowField.id)) {
          fieldMap.set(workflowField.id, {
            values: new Set(),
            itemIds: new Set(),
            field: workflowField,
          });
        }

        const itemField = item.taskItemFields?.find((f) => f.fieldId === workflowField.id);

        const taskField = tasks
          ?.find((t) => t.id === item.taskId)
          ?.taskFields?.find((f) => f.fieldId === workflowField.id);

        const resolvedValue = itemField?.value || taskField?.value;

        if (resolvedValue) {
          const entry = fieldMap.get(workflowField.id)!;
          entry.values.add(resolvedValue);
          entry.itemIds.add(item.id);
        }
      });
    });

    return Array.from(fieldMap.values()).map(({ values, itemIds, field }) => ({
      ...field,
      values: Array.from(values),
      itemIds: Array.from(itemIds),
      isMixed: values.size > 1 || (values.size === 1 && itemIds.size < items.length),
    }));
  }, [items, tasks, workflowFieldsMap]);

  if (!items.length) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className={cn({ "text-white": isDark, "text-gray-500": !isDark })}>No item selected</p>
      </div>
    );
  }

  const showLoading = isLoading || isLoadingFields || !areFieldsReady;

  return (
    <div className="flex-1 overflow-auto">
      {showLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600" />
        </div>
      )}

      {!showLoading && displayFields.length === 0 && (
        <div className="flex items-center justify-center py-8">
          <p className={cn({ "text-white": isDark, "text-gray-500": !isDark })}>
            No metadata fields available
          </p>
        </div>
      )}

      {!showLoading && displayFields.length > 0 && (
        <div className="flex h-full flex-col overflow-auto">
          <div className="flex-1 p-3">
            <ItemMetaTable
              itemFields={displayFields}
              items={items}
              apiBaseUrl={apiBaseUrl}
              isDark={isDark}
              windowId={windowId}
              useFieldMutations={useFieldMutations}
              useTasksInfo={useTasksInfo}
            />
          </div>
        </div>
      )}
    </div>
  );
};
